import { useScrollReveal } from "@/hooks/useScrollReveal";
import { projects, type Project } from "@/data/projects";
import { ExternalLink, Github } from "lucide-react";

export function Projects() {
  const headerRef = useScrollReveal<HTMLDivElement>({ y: 30 });

  return (
    <section id="projects" className="min-h-screen px-6 py-24">
      <div className="max-w-3xl mx-auto">
        <div ref={headerRef} className="mb-12">
          <div data-reveal className="flex items-center gap-3 mb-2">
            <span className="text-terminal-green">$</span>
            <span className="text-terminal-cyan">find</span>
            <span className="text-foreground">./projects -type d</span>
          </div>
          <h2 data-reveal className="text-2xl font-bold text-terminal-bright">
            Projects
          </h2>
          <p data-reveal className="text-muted-foreground mt-1">
            {projects.length} directories found
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {projects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ project, index }: { project: Project; index: number }) {
  const ref = useScrollReveal<HTMLDivElement>({ y: 30, delay: index * 0.1 });

  return (
    <div
      ref={ref}
      data-reveal
      className="border border-border rounded-lg p-5 bg-card hover:border-terminal-green/30 transition-all group"
    >
      <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
        <span className="text-terminal-dim">~/projects/</span>
        <span className="text-terminal-cyan">{project.id}</span>
        {project.featured && (
          <span className="ml-auto text-terminal-yellow">★ featured</span>
        )}
      </div>

      <h3 className="text-lg font-bold text-terminal-bright mb-2 group-hover:text-terminal-green transition-colors">
        {project.title}
      </h3>

      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        {project.description}
      </p>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {project.techStack.map((tech) => (
          <span
            key={tech}
            className="text-xs px-2 py-0.5 rounded bg-secondary text-terminal-cyan"
          >
            {tech}
          </span>
        ))}
      </div>

      <div className="flex gap-4 text-sm">
        {project.repoUrl && (
          <a
            href={project.repoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-muted-foreground hover:text-terminal-green transition-colors"
          >
            <Github size={14} />
            <span>source</span>
          </a>
        )}
        {project.liveUrl && (
          <a
            href={project.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-muted-foreground hover:text-terminal-cyan transition-colors"
          >
            <ExternalLink size={14} />
            <span>live</span>
          </a>
        )}
      </div>
    </div>
  );
}
